import { type Theme } from '../../../theme';
import type { CSSObject } from '@emotion/react';
import { IconButtonSize, IconButtonType } from './iconButton';

export const getStylesBySize = (
  size: IconButtonSize,
  theme: Theme,
): CSSObject =>
  ({
    xSmall: {
      borderRadius: theme.shape.radius4,
      padding: '2px',
      height: 20,
      width: 20,
      fontSize: '16px',
      '& svg': {
        width: '16px',
        height: '16px',
      },
    },
    small: {
      borderRadius: theme.shape.radius4,
      padding: '2px',
      height: 24,
      width: 24,
      fontSize: '16px',
      '& svg': {
        width: '16px',
        height: '16px',
      },
    },
    medium: {
      borderRadius: theme.shape.radius8,
      padding: `6px`,
      height: 32,
      width: 32,
      fontSize: '20px',
      '& svg': {
        width: '20px',
        height: '20px',
      },
    },
    large: {
      borderRadius: theme.shape.radius8,
      padding: theme.spacing(2),
      height: 40,
      width: 40,
      fontSize: '20px',
      '& svg': {
        width: '24px',
        height: '24px',
      },
    },
  })[size];

export const getBaseStyles = (theme: Theme): CSSObject => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  border: 'none',
  transition:
    'background 200ms ease-in-out, color 200ms ease-in-out, border 200ms ease-in-out',
  color: theme.icon.primary,
  WebkitTapHighlightColor: 'transparent',
});

export const getStylesByType = (
  type: IconButtonType,
  active: boolean,
  theme: Theme,
): CSSObject =>
  ({
    default: {
      backgroundColor: active
        ? theme.iconButton.background.default.active
        : theme.iconButton.background.default.default,
      '&:hover': {
        backgroundColor: theme.iconButton.background.default.hover,
      },
      '&:active, &:focus:not(:focus-visible)': {
        backgroundColor: theme.iconButton.background.default.pressed,
        outline: 'none',
      },
      '&:focus': {
        outline: `1px solid ${theme.border.interactive.default}`,
        backgroundColor: theme.iconButton.background.default.focused,
      },
      '&:disabled': {
        color: theme.icon.interactive.disabled,
        backgroundColor: theme.iconButton.background.default.disabled,
        '&:hover': {
          color: theme.icon.interactive.disabled,
          backgroundColor: theme.iconButton.background.default.disabledHover,
        },
      },
    },
    defaultAlpha: {
      backgroundColor: active
        ? theme.iconButton.background.defaultAlpha.active
        : theme.iconButton.background.defaultAlpha.default,
      backdropFilter: theme.backdropFilter.xlarge,
      '&:hover': {
        backgroundColor: theme.iconButton.background.defaultAlpha.hover,
      },
      '&:active, &:focus:not(:focus-visible)': {
        backgroundColor: theme.iconButton.background.defaultAlpha.pressed,
        outline: 'none',
      },
      '&:focus': {
        outline: `1px solid ${theme.border.interactive.default}`,
        backgroundColor: theme.iconButton.background.defaultAlpha.focused,
      },
      '&:disabled': {
        color: theme.icon.interactive.disabled,
        backgroundColor: theme.iconButton.background.defaultAlpha.disabled,
        '&:hover': {
          color: theme.icon.interactive.disabled,
          backgroundColor:
            theme.iconButton.background.defaultAlpha.disabledHover,
        },
      },
    },
    subtle: {
      backgroundColor: active
        ? theme.iconButton.background.subtle.active
        : theme.iconButton.background.subtle.default,
      '&:hover': {
        backgroundColor: theme.iconButton.background.subtle.hover,
      },
      '&:active, &:focus:not(:focus-visible)': {
        backgroundColor: theme.iconButton.background.subtle.pressed,
        outline: 'none',
      },
      '&:focus': {
        outline: `1px solid ${theme.border.interactive.default}`,
        backgroundColor: theme.iconButton.background.subtle.focused,
      },
      '&:disabled': {
        color: theme.icon.interactive.disabled,
        backgroundColor: theme.iconButton.background.subtle.disabled,
        '&:hover': {
          color: theme.icon.interactive.disabled,
          backgroundColor: theme.iconButton.background.subtle.disabledHover,
        },
      },
    },
    tinted: {
      backgroundColor: active
        ? theme.iconButton.background.tinted.active
        : theme.iconButton.background.tinted.default,
      '&:hover': {
        backgroundColor: theme.iconButton.background.tinted.hover,
      },
      '&:active, &:focus:not(:focus-visible)': {
        backgroundColor: theme.iconButton.background.tinted.pressed,
        outline: 'none',
      },
      '&:focus': {
        outline: `1px solid ${theme.border.interactive.default}`,
        backgroundColor: theme.iconButton.background.tinted.focused,
      },
      '&:disabled': {
        color: theme.icon.interactive.disabled,
        backgroundColor: theme.iconButton.background.tinted.disabled,
        '&:hover': {
          color: theme.icon.interactive.disabled,
          backgroundColor: theme.iconButton.background.tinted.disabledHover,
        },
      },
    },
    tintedWithOutline: {
      '&:hover': {
        backgroundColor: theme.iconButton.background.tinted.hover,
        border: `1px solid ${theme.border.interactive.hover}`,
      },
      '&:active, &:focus:not(:focus-visible)': {
        backgroundColor: theme.iconButton.background.tinted.pressed,
        border: `1px solid ${theme.border.interactive.hover}`,
        outline: 'none',
      },
      '&:focus': {
        outline: 'none',
        backgroundColor: theme.iconButton.background.tinted.focused,
        border: `1px solid ${theme.border.interactive.default}`,
      },
      '&:disabled': {
        color: theme.icon.interactive.disabled,
        backgroundColor: theme.iconButton.background.tinted.disabled,
        '&:hover': {
          color: theme.icon.interactive.disabled,
          backgroundColor: theme.iconButton.background.tinted.disabledHover,
          border: `1px solid ${theme.iconButton.background.tintedWithStroke.border}`,
        },
      },
      backgroundColor: active
        ? theme.iconButton.background.tintedWithStroke.active
        : theme.iconButton.background.tintedWithStroke.default,
      border: `1px solid ${theme.iconButton.background.tintedWithStroke.border}`,
    },
  })[type];

export const getDestructiveStylesForType = (
  type: IconButtonType,
  active: boolean,
  theme: Theme,
): CSSObject =>
  ({
    default: {
      backgroundColor: active
        ? theme.iconButton.background.defaultDestructive.active
        : theme.iconButton.background.defaultDestructive.default,
      color: active ? theme.iconButton.icon.destructive : theme.icon.primary,
      '&:hover': {
        backgroundColor: theme.iconButton.background.defaultDestructive.hover,
      },
      '&:focus:not(:focus-visible)': {
        backgroundColor: theme.iconButton.background.defaultDestructive.pressed,
        outline: 'none',
      },
      '&:active': {
        backgroundColor: theme.iconButton.background.defaultDestructive.active,
        outline: 'none',
      },
      '&:focus': {
        outline: `1px solid ${theme.border.interactive.negative}`,
        backgroundColor: theme.iconButton.background.defaultDestructive.focused,
      },
      '&:disabled': {
        color: theme.icon.interactive.disabled,
        backgroundColor: theme.iconButton.background.default.disabled,
        '&:hover': {
          color: theme.icon.interactive.disabled,
          backgroundColor:
            theme.iconButton.background.defaultDestructive.disabledHover,
        },
      },
    },
    defaultAlpha: {
      backgroundColor: active
        ? theme.iconButton.background.defaultAlphaDestructive.active
        : theme.iconButton.background.defaultAlpha.default,
      color: active ? theme.iconButton.icon.destructive : theme.icon.primary,
      '&:hover': {
        backgroundColor:
          theme.iconButton.background.defaultAlphaDestructive.hover,
      },
      '&:focus:not(:focus-visible)': {
        backgroundColor:
          theme.iconButton.background.defaultAlphaDestructive.pressed,
        outline: 'none',
      },
      '&:active': {
        backgroundColor:
          theme.iconButton.background.defaultAlphaDestructive.active,
        outline: 'none',
      },
      '&:focus': {
        outline: `1px solid ${theme.border.interactive.negative}`,
        backgroundColor:
          theme.iconButton.background.defaultAlphaDestructive.focused,
      },
      '&:disabled': {
        color: theme.icon.interactive.disabled,
        backgroundColor: theme.iconButton.background.subtleDestructive.disabled,
        '&:hover': {
          color: theme.icon.interactive.disabled,
          backgroundColor:
            theme.iconButton.background.subtleDestructive.disabledHover,
        },
      },
    },
    subtle: {
      backgroundColor: active
        ? theme.iconButton.background.subtleDestructive.active
        : theme.iconButton.background.subtleDestructive.default,
      color: active ? theme.iconButton.icon.destructive : theme.icon.primary,
      '&:hover': {
        backgroundColor: theme.iconButton.background.subtleDestructive.hover,
      },
      '&:active': {
        backgroundColor: theme.iconButton.background.subtleDestructive.pressed,
        outline: 'none',
      },
      '&:focus:not(:focus-visible)': {
        backgroundColor: theme.iconButton.background.subtleDestructive.active,
        outline: 'none',
      },
      '&:focus': {
        outline: `1px solid ${theme.border.interactive.negative}`,
        backgroundColor: theme.iconButton.background.subtleDestructive.focused,
      },
      '&:disabled': {
        color: theme.icon.interactive.disabled,
        backgroundColor: theme.iconButton.background.subtleDestructive.disabled,
        '&:hover': {
          color: theme.icon.interactive.disabled,
          backgroundColor:
            theme.iconButton.background.subtleDestructive.disabledHover,
        },
      },
    },
    tinted: {
      backgroundColor: active
        ? theme.iconButton.background.tintedDestructive.active
        : theme.iconButton.background.tintedDestructive.default,
      color: active ? theme.iconButton.icon.destructive : theme.icon.primary,
      '&:hover': {
        backgroundColor: theme.iconButton.background.tintedDestructive.hover,
      },
      '&:active': {
        backgroundColor: theme.iconButton.background.tintedDestructive.pressed,
        outline: 'none',
      },
      '&:focus:not(:focus-visible)': {
        backgroundColor: theme.iconButton.background.tintedDestructive.active,
        outline: 'none',
      },
      '&:focus': {
        outline: `1px solid ${theme.border.interactive.negative}`,
        backgroundColor: theme.iconButton.background.tintedDestructive.focused,
      },
      '&:disabled': {
        color: theme.icon.interactive.disabled,
        backgroundColor: theme.iconButton.background.tintedDestructive.disabled,
        '&:hover': {
          color: theme.icon.interactive.disabled,
          backgroundColor:
            theme.iconButton.background.tintedDestructive.disabledHover,
        },
      },
    },
    tintedWithOutline: {
      backgroundColor: active
        ? theme.iconButton.background.tintedwithstrokeDestructive.active
        : theme.iconButton.background.tintedwithstrokeDestructive.default,
      color: active ? theme.iconButton.icon.destructive : theme.icon.primary,
      border: `1px solid ${theme.iconButton.background.tintedWithStroke.border}`,
      '&:hover': {
        backgroundColor:
          theme.iconButton.background.tintedwithstrokeDestructive.hover,
        border: `1px solid ${theme.border.interactive.negativeHover}`,
      },
      '&:active': {
        backgroundColor:
          theme.iconButton.background.tintedwithstrokeDestructive.pressed,
        border: `1px solid ${theme.border.interactive.negative}`,
        outline: 'none',
      },
      '&:focus:not(:focus-visible)': {
        backgroundColor:
          theme.iconButton.background.tintedwithstrokeDestructive.active,
        border: `1px solid ${theme.border.interactive.negative}`,
        outline: 'none',
      },
      '&:focus': {
        border: `1px solid ${theme.border.interactive.negative}`,
        outline: 'none',
        backgroundColor:
          theme.iconButton.background.tintedwithstrokeDestructive.focused,
      },
      '&:disabled': {
        color: theme.icon.interactive.disabled,
        backgroundColor:
          theme.iconButton.background.tintedwithstrokeDestructive.disabled,
        border: `1px solid ${theme.iconButton.background.tintedwithstrokeDestructive.border}`,
        '&:hover': {
          color: theme.icon.interactive.disabled,
          backgroundColor:
            theme.iconButton.background.tintedwithstrokeDestructive
              .disabledHover,
        },
      },
    },
  })[type];
